import { useMemo, useState, useEffect, useRef, type JSX, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AsyncState } from '@/components/async';
import { Skeleton } from '@/components/feedback';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { LeadShowingsPanel } from '@/components/dashboard/showings/LeadShowingsPanel';
import { DashboardFilterBar } from '@/components/dashboard/DashboardFilterBar';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useLeadDetail, useLeadsByPhone, useLeadTimeline } from '@/hooks/useLeads';
import {
  format as formatDateFns,
  formatDistanceToNow,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import {
  updateLeadForUser,
  scheduleLeadFollowUpForUser,
  logLeadContactForUser,
} from '@/lib/services/leads';
import {
  trackLeadStageChange,
  trackLeadFollowUpScheduled,
  trackLeadContactLogged,
} from '@/lib/analytics/events';
import {
  LEAD_CAMPAIGN_MEDIUM_CONTACT,
  LEAD_CAMPAIGN_SOURCE_CONTACT,
  LEAD_STAGE_OPTIONS,
  formatLeadStage,
} from '@/lib/constants/leads';
import type {
  LeadDetail as LeadDetailType,
  LeadFormValues,
  LeadSummary,
  LeadStage,
  LeadTimelineEvent,
  LeadTimelineEventType,
} from '@/lib/types/lead';
import { USER_ROLES, type AppUser } from '@/lib/auth';

export function LeadDetail(): JSX.Element {
  const user = useDashboardUser();
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();

  const normalizedLeadId = typeof leadId === 'string' ? leadId.trim() : '';

  const detailQuery = useLeadDetail({
    user,
    leadId: normalizedLeadId.length > 0 ? normalizedLeadId : null,
  });

  const timelineQuery = useLeadTimeline({
    user,
    leadId: normalizedLeadId.length > 0 ? normalizedLeadId : null,
    limit: 50,
  });

  const relatedLeadsQuery = useLeadsByPhone({
    user,
    phone: detailQuery.data?.phone ?? null,
    limit: 50,
  });

  const relatedLeads = useMemo<LeadSummary[]>(() => {
    const entries = new Map<string, LeadSummary>();
    if (detailQuery.data) {
      entries.set(detailQuery.data.id, detailQuery.data);
    }
    (relatedLeadsQuery.data ?? []).forEach((lead) => {
      entries.set(lead.id, lead);
    });
    return Array.from(entries.values());
  }, [detailQuery.data, relatedLeadsQuery.data]);

  const relatedLeadIds = useMemo(() => relatedLeads.map((lead) => lead.id), [relatedLeads]);

  const contactSubmissions = useMemo(() => {
    const filtered = relatedLeads.filter(
      (lead) =>
        lead.campaignSource === LEAD_CAMPAIGN_SOURCE_CONTACT &&
        lead.campaignMedium === LEAD_CAMPAIGN_MEDIUM_CONTACT,
    );
    return filtered.sort((a, b) => compareIsoDatesDesc(a.createdAt, b.createdAt));
  }, [relatedLeads]);

  const updateMutation = useMutation({
    mutationFn: async ({ leadId: targetId, values }: { leadId: string; values: LeadFormValues }) =>
      updateLeadForUser({ user, leadId: targetId, values }),
    onSuccess: (_data, variables) => {
      toast.success('Lead updated');
      const newStage = variables.values.stage;
      const previousStage = detailQuery.data?.stage ?? null;
      if (normalizedLeadId.length > 0 && previousStage !== null && previousStage !== newStage) {
        trackLeadStageChange({
          leadId: normalizedLeadId,
          stage: newStage,
          previousStage,
          origin: 'dashboard.lead-detail',
        });
      }
      detailQuery.refetch().catch((error: unknown) => {
        console.error('Failed to refresh lead detail', error);
      });
      timelineQuery.refetch().catch((error: unknown) => {
        console.error('Failed to refresh lead timeline', error);
      });
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update lead');
      }
    },
  });

  const scheduleFollowUpMutation = useMutation({
    mutationFn: async (nextFollowUpAt: string | null) => {
      if (normalizedLeadId.length === 0) {
        throw new Error('Missing lead identifier.');
      }
      return scheduleLeadFollowUpForUser({
        user,
        leadId: normalizedLeadId,
        nextFollowUpAt,
      });
    },
    onSuccess: async (data) => {
      toast.success('Follow-up updated');
      if (normalizedLeadId.length > 0) {
        const previousNextFollowUpAt = detailQuery.data?.nextFollowUpAt ?? null;
        let refreshedDetail: Awaited<ReturnType<typeof detailQuery.refetch>> | null = null;

        try {
          refreshedDetail = await detailQuery.refetch();
        } catch (error) {
          console.error('Failed to refresh lead detail', error);
        }

        const refreshedNextFollowUpAt = refreshedDetail?.data?.nextFollowUpAt;
        const nextFollowUpAt =
          refreshedNextFollowUpAt !== undefined
            ? (refreshedNextFollowUpAt ?? null)
            : (data.nextFollowUpAt ?? null);
        trackLeadFollowUpScheduled({
          leadId: normalizedLeadId,
          nextFollowUpAt,
          previousNextFollowUpAt,
          sourceAction: nextFollowUpAt !== null ? 'schedule' : 'clear',
          origin: 'dashboard.lead-detail',
        });
      } else {
        detailQuery.refetch().catch((error: unknown) => {
          console.error('Failed to refresh lead detail', error);
        });
      }
      timelineQuery.refetch().catch((error: unknown) => {
        console.error('Failed to refresh lead timeline', error);
      });
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update follow-up');
      }
    },
  });

  const logContactMutation = useMutation({
    mutationFn: async () => {
      if (normalizedLeadId.length === 0) {
        throw new Error('Missing lead identifier.');
      }
      return logLeadContactForUser({ user, leadId: normalizedLeadId });
    },
    onSuccess: () => {
      toast.success('Contact logged');
      if (normalizedLeadId.length > 0) {
        const contactTimestamp = new Date().toISOString();
        trackLeadContactLogged({
          leadId: normalizedLeadId,
          contactedAt: contactTimestamp,
          nextFollowUpAt: detailQuery.data?.nextFollowUpAt ?? null,
          origin: 'dashboard.lead-detail',
        });
      }
      detailQuery.refetch().catch((error: unknown) => {
        console.error('Failed to refresh lead detail', error);
      });
      timelineQuery.refetch().catch((error: unknown) => {
        console.error('Failed to refresh lead timeline', error);
      });
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to log contact');
      }
    },
  });

  return (
    <section className="flex flex-col gap-xl py-8">
      <header className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-xs">
          <button
            type="button"
            onClick={() => {
              navigate('/dashboard/leads');
            }}
            className="min-h-11 w-fit rounded-full border border-brand-primary/30 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            Back to leads
          </button>
          <h1 className="text-2xl font-semibold text-brand-primary md:text-3xl">Lead details</h1>
          <p className="text-sm text-text-primary/70">
            Review prospect context, adjust stage, and coordinate follow-ups with the leasing team.
          </p>
        </div>
      </header>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={<LeadDetailSkeleton />}
        errorFallback={<LeadDetailError />}
      >
        {(detail) => {
          const canManageLead =
            user.role !== USER_ROLES.manager ||
            detail.assignedTo === user.id ||
            detail.assignedTo === null;

          return (
            <LeadDetailContent
              lead={detail}
              isUpdating={updateMutation.isPending}
              currentUserId={user.id}
              currentUserRole={user.role}
              canManageLead={canManageLead}
              isScheduling={scheduleFollowUpMutation.isPending}
              isLogging={logContactMutation.isPending}
              relatedLeadIds={relatedLeadIds}
              contactSubmissions={contactSubmissions}
              isContactSubmissionsLoading={
                relatedLeadsQuery.isLoading || relatedLeadsQuery.isFetching
              }
              timelineEvents={timelineQuery.data ?? []}
              isTimelineLoading={timelineQuery.isLoading || timelineQuery.isFetching}
              onUpdate={(values) => {
                if (normalizedLeadId.length === 0) {
                  return;
                }
                updateMutation.mutate({ leadId: normalizedLeadId, values });
              }}
              onScheduleFollowUp={(value) => {
                scheduleFollowUpMutation.mutate(value);
              }}
              onLogContact={() => {
                logContactMutation.mutate();
              }}
            />
          );
        }}
      </AsyncState>
    </section>
  );
}

function LeadDetailContent({
  lead,
  isUpdating,
  currentUserId,
  currentUserRole,
  canManageLead,
  isScheduling,
  isLogging,
  relatedLeadIds,
  contactSubmissions,
  isContactSubmissionsLoading,
  timelineEvents,
  isTimelineLoading,
  onUpdate,
  onScheduleFollowUp,
  onLogContact,
}: {
  lead: LeadDetailType;
  isUpdating: boolean;
  currentUserId: string;
  currentUserRole: AppUser['role'];
  canManageLead: boolean;
  isScheduling: boolean;
  isLogging: boolean;
  relatedLeadIds: string[];
  contactSubmissions: LeadSummary[];
  isContactSubmissionsLoading: boolean;
  timelineEvents: LeadTimelineEvent[];
  isTimelineLoading: boolean;
  onUpdate: (values: LeadFormValues) => void;
  onScheduleFollowUp: (nextFollowUpAt: string | null) => void;
  onLogContact: () => void;
}): JSX.Element {
  const formValues = useMemo(() => mapLeadDetailToFormValues(lead), [lead]);
  const [nextFollowUpInput, setNextFollowUpInput] = useState<string>(
    formatDateTimeLocalInput(lead.nextFollowUpAt),
  );
  const nextFollowUpInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setNextFollowUpInput(formatDateTimeLocalInput(lead.nextFollowUpAt));
  }, [lead.nextFollowUpAt]);

  const handleScheduleFollowUp = () => {
    if (!canManageLead) {
      return;
    }
    const rawValue = nextFollowUpInputRef.current?.value ?? nextFollowUpInput;
    const isoValue = parseDateTimeLocalInput(rawValue);
    onScheduleFollowUp(isoValue);
  };

  const handleLogContact = () => {
    if (!canManageLead) {
      return;
    }
    onLogContact();
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
      <div className="flex flex-col gap-8">
        <section className="rounded-3xl border border-surface-subtle bg-white p-8 shadow-card">
          <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold text-text-primary">{lead.fullName}</h2>
              <div className="flex flex-wrap gap-2 text-sm text-text-primary/70">
                <span>{lead.email}</span>
                {typeof lead.phone === 'string' && lead.phone.length > 0 ? (
                  <span aria-hidden="true">•</span>
                ) : null}
                {typeof lead.phone === 'string' && lead.phone.length > 0 ? (
                  <span>{lead.phone}</span>
                ) : null}
              </div>
            </div>
            <StatusBadge label={formatLeadStage(lead.stage)} tone="info" />
          </header>

          <DashboardFilterBar className="mt-6" containerClassName="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                Stage
              </label>
              <select
                value={lead.stage}
                onChange={(event) => {
                  onUpdate({ ...formValues, stage: event.target.value as LeadFormValues['stage'] });
                }}
                disabled={isUpdating || (!canManageLead && currentUserRole === USER_ROLES.manager)}
                className="rounded-xl border border-surface-subtle bg-white px-4 py-3 text-base shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
              >
                {LEAD_STAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                Campaign source
              </label>
              <span className="rounded-xl border border-dashed border-surface-subtle bg-surface-light px-4 py-3 text-sm text-text-primary/80">
                {lead.campaignSource ?? '—'}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                Campaign medium
              </label>
              <span className="rounded-xl border border-dashed border-surface-subtle bg-surface-light px-4 py-3 text-sm text-text-primary/80">
                {lead.campaignMedium ?? '—'}
              </span>
            </div>
          </DashboardFilterBar>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <LeadMeta label="Budget" value={formatCurrency(lead.budgetMonthly)} />
            <LeadMeta label="Preferred move-in" value={formatDate(lead.preferredMoveInDate)} />
            <LeadMeta label="Last contacted" value={formatDateTime(lead.lastContactedAt)} />
            <LeadMeta label="Next follow-up" value={formatDateTime(lead.nextFollowUpAt)} />
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
              Notes
            </h3>
            <p className="rounded-2xl border border-surface-subtle bg-surface-light px-4 py-4 text-sm leading-relaxed text-text-primary/80">
              {lead.notes ?? 'No notes captured yet.'}
            </p>
          </div>
        </section>

        <ContactSubmissionsPanel
          submissions={contactSubmissions}
          isLoading={isContactSubmissionsLoading}
          currentLeadId={lead.id}
          relatedLeadCount={relatedLeadIds.length}
        />

        <LeadShowingsPanel leadIds={relatedLeadIds} />

        <section className="rounded-3xl border border-surface-subtle bg-white p-8 shadow-card">
          <h3 className="text-lg font-semibold text-text-primary">Timeline</h3>
          <LeadTimeline events={timelineEvents} isLoading={isTimelineLoading} />
        </section>
      </div>

      <aside className="flex flex-col gap-6">
        <section className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Assignment
          </h3>
          <div className="mt-2 flex flex-col gap-3">
            <p className="text-sm text-text-primary/70">
              Assigned to:{' '}
              <span className="font-semibold text-text-primary">
                {lead.assignedTo ?? 'Unassigned'}
              </span>
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={isUpdating || lead.assignedTo === currentUserId}
                onClick={() => {
                  onUpdate({ ...formValues, assignedTo: currentUserId });
                }}
                className="w-full rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Assign to me
              </button>
              <button
                type="button"
                disabled={isUpdating || lead.assignedTo === null}
                onClick={() => {
                  onUpdate({ ...formValues, assignedTo: null });
                }}
                className="w-full rounded-full border border-surface-subtle px-4 py-2 text-xs font-semibold uppercase tracking-wide text-text-primary transition hover:bg-surface-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear assignment
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Follow-up reminders
          </h3>
          <div className="mt-3 flex flex-col gap-3">
            <label
              htmlFor="lead-follow-up"
              className="text-xs font-semibold uppercase tracking-wide text-text-primary/60"
            >
              Next follow-up
            </label>
            <input
              id="lead-follow-up"
              type="datetime-local"
              value={nextFollowUpInput}
              onChange={(event) => {
                setNextFollowUpInput(event.target.value);
              }}
              ref={nextFollowUpInputRef}
              className="rounded-xl border border-surface-subtle px-4 py-3 text-base shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
              disabled={isScheduling || !canManageLead}
            />
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleScheduleFollowUp}
                disabled={isScheduling || !canManageLead}
              >
                Save follow-up
              </button>
              <button
                type="button"
                className="rounded-full border border-surface-subtle px-4 py-2 text-xs font-semibold uppercase tracking-wide text-text-primary transition hover:bg-surface-light disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setNextFollowUpInput('');
                  onScheduleFollowUp(null);
                }}
                disabled={isScheduling || !canManageLead}
              >
                Clear follow-up
              </button>
              <button
                type="button"
                className="rounded-full bg-brand-primary px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleLogContact}
                disabled={isLogging || !canManageLead}
              >
                Log contact now
              </button>
            </div>
            {canManageLead || currentUserRole !== USER_ROLES.manager ? (
              <p className="text-xs text-text-primary/60">
                Logging contact updates the last contacted timestamp. Saving a follow-up stores the
                next reminder for this lead.
              </p>
            ) : (
              <p className="text-xs text-text-primary/60">
                This lead is assigned to another manager. Reassign it to yourself to manage
                follow-up reminders and log contacts.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Opportunity summary
          </h3>
          <dl className="mt-3 space-y-3 text-sm text-text-primary/80">
            <div className="flex justify-between gap-3">
              <dt>Desired property</dt>
              <dd>{lead.desiredPropertyId ?? 'Not specified'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>Desired unit</dt>
              <dd>{lead.desiredUnitId ?? 'Not specified'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>Loss reason</dt>
              <dd>{lead.lossReason ?? '—'}</dd>
            </div>
          </dl>
        </section>
      </aside>
    </div>
  );
}

function mapLeadDetailToFormValues(lead: LeadDetailType): LeadFormValues {
  return {
    fullName: lead.fullName,
    email: lead.email,
    phone: lead.phone,
    campaignSource: lead.campaignSource,
    campaignMedium: lead.campaignMedium,
    desiredPropertyId: lead.desiredPropertyId,
    desiredUnitId: lead.desiredUnitId,
    stage: lead.stage,
    assignedTo: lead.assignedTo,
    budgetMonthly: lead.budgetMonthly,
    preferredMoveInDate: lead.preferredMoveInDate,
    lastContactedAt: lead.lastContactedAt,
    nextFollowUpAt: lead.nextFollowUpAt,
    notes: lead.notes,
    lossReason: lead.lossReason,
  };
}

function LeadMeta({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col rounded-2xl border border-surface-subtle bg-surface-light px-4 py-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
        {label}
      </span>
      <span className="text-sm font-medium text-text-primary">{value}</span>
    </div>
  );
}

function ContactSubmissionsPanel({
  submissions,
  isLoading,
  currentLeadId,
  relatedLeadCount,
}: {
  submissions: LeadSummary[];
  isLoading: boolean;
  currentLeadId: string;
  relatedLeadCount: number;
}): JSX.Element {
  const relatedLeadLabel =
    relatedLeadCount > 1 ? `Matched ${relatedLeadCount.toString()} lead records by phone.` : null;

  return (
    <section className="rounded-3xl border border-surface-subtle bg-white p-8 shadow-card">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-text-primary">Contact submissions</h3>
          <p className="text-sm text-text-primary/70">Messages captured from the contact form.</p>
          {relatedLeadLabel ? (
            <p className="text-xs text-text-primary/60">{relatedLeadLabel}</p>
          ) : null}
        </div>
        <StatusBadge label={`${submissions.length.toString()} total`} tone="neutral" />
      </header>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex flex-col gap-3" aria-label="Loading contact submissions">
            <Skeleton className="h-20 w-full rounded-2xl" />
            <Skeleton className="h-20 w-full rounded-2xl" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-surface-subtle bg-surface-light px-6 py-8 text-sm text-text-primary/70">
            No contact form submissions yet.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {submissions.map((submission) => {
              const message = formatContactSubmissionMessage(submission.notes);
              const fullMessage =
                typeof submission.notes === 'string' ? submission.notes.trim() : '';
              return (
                <article
                  key={submission.id}
                  className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-surface-light px-5 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold text-text-primary">
                        {formatDateTime(submission.createdAt)}
                      </p>
                      <p className="text-xs text-text-primary/70">{submission.email}</p>
                    </div>
                    {submission.id === currentLeadId ? (
                      <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/50">
                        Current lead
                      </span>
                    ) : (
                      <Link
                        to={`/dashboard/leads/${submission.id}`}
                        className="text-xs font-semibold uppercase tracking-wide text-brand-primary hover:underline"
                      >
                        View lead
                      </Link>
                    )}
                  </div>
                  <p
                    className="whitespace-pre-line text-sm text-text-primary/80"
                    title={fullMessage}
                  >
                    {message}
                  </p>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function formatCurrency(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value: string | null): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '—';
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(parsed));
}

function formatDateTime(value: string | null): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '—';
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(parsed));
}

function formatContactSubmissionMessage(notes: string | null): string {
  if (typeof notes !== 'string') {
    return '—';
  }

  const trimmed = notes.trim();
  if (trimmed.length === 0) {
    return '—';
  }

  const firstLine = trimmed.split(/\r?\n/)[0] ?? '';
  if (firstLine.length <= 120) {
    return firstLine;
  }

  return `${firstLine.slice(0, 117)}...`;
}

function compareIsoDatesDesc(valueA: string | null, valueB: string | null): number {
  const parsedA = typeof valueA === 'string' ? Date.parse(valueA) : Number.NaN;
  const parsedB = typeof valueB === 'string' ? Date.parse(valueB) : Number.NaN;

  if (Number.isNaN(parsedA) && Number.isNaN(parsedB)) {
    return 0;
  }
  if (Number.isNaN(parsedA)) {
    return 1;
  }
  if (Number.isNaN(parsedB)) {
    return -1;
  }

  return parsedB - parsedA;
}

function LeadTimeline({
  events,
  isLoading,
}: {
  events: LeadTimelineEvent[];
  isLoading: boolean;
}): JSX.Element {
  const groupedEvents = useMemo(() => groupTimelineEvents(events), [events]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 pt-4">
        {[...Array(3).keys()].map((index) => (
          <Skeleton
            key={`timeline-skeleton-${String(index)}`}
            className="h-20 w-full rounded-2xl"
          />
        ))}
      </div>
    );
  }

  if (groupedEvents.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-surface-subtle bg-surface-light px-6 py-8 text-sm text-text-primary/70">
        No timeline entries yet. Update stage, log contact, or set a follow-up to see activity here
        once telemetry events are generated.
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-6">
      {groupedEvents.map((group) => (
        <section key={group.key} className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {group.label}
          </h3>
          <ol className="flex flex-col gap-4">
            {group.events.map((event) => {
              const label = formatTimelineLabel(event.eventType);
              const relativeTime = formatTimelineRelativeTime(event.recordedAt);
              const absoluteTime = formatDateTime(event.recordedAt);
              const detail = renderTimelineDetails(event);
              const recordedBy = safeString(event.recordedByDisplayName);

              return (
                <li
                  key={event.id}
                  className="bg-surface-base/60 rounded-2xl border border-surface-subtle p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/70">
                      {label}
                    </span>
                    <time
                      className="text-xs text-text-primary/60"
                      dateTime={event.recordedAt}
                      title={absoluteTime}
                    >
                      {relativeTime}
                    </time>
                  </div>
                  <div className="mt-3 flex flex-col gap-3 text-sm text-text-primary">{detail}</div>
                  {recordedBy !== null ? (
                    <span className="mt-3 block text-xs text-text-primary/60">by {recordedBy}</span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

function formatTimelineLabel(eventType: LeadTimelineEventType): string {
  switch (eventType) {
    case 'captured':
      return 'Lead captured';
    case 'stage_change':
      return 'Stage updated';
    case 'follow_up_set':
      return 'Follow-up scheduled';
    case 'follow_up_cleared':
      return 'Follow-up cleared';
    case 'contact_logged':
      return 'Contact logged';
    case 'assignment':
      return 'Assignment updated';
    case 'note':
      return 'Note updated';
    default:
      return 'Activity recorded';
  }
}

interface TimelineGroup {
  key: string;
  label: string;
  events: LeadTimelineEvent[];
}

function groupTimelineEvents(events: LeadTimelineEvent[]): TimelineGroup[] {
  const groups: TimelineGroup[] = [];
  const groupMap = new Map<string, TimelineGroup>();

  events.forEach((event, index) => {
    const parsed = parseTimelineDate(event.recordedAt);
    const day = parsed !== null ? startOfDay(parsed) : null;
    const key = day !== null ? day.toISOString() : `undated-${String(index)}`;

    let group = groupMap.get(key);
    if (!group) {
      group = {
        key,
        label: day !== null ? formatDateFns(day, 'MMMM d, yyyy') : 'Undated activity',
        events: [],
      };
      groupMap.set(key, group);
      groups.push(group);
    }

    group.events.push(event);
  });

  return groups;
}

function parseTimelineDate(value: string | null | undefined): Date | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  const parsed = parseISO(value);
  if (isValid(parsed)) {
    return parsed;
  }

  const fallback = new Date(value);
  if (Number.isNaN(fallback.getTime())) {
    return null;
  }

  return fallback;
}

function formatTimelineRelativeTime(value: string | null | undefined): string {
  const parsed = parseTimelineDate(value ?? null);
  if (parsed === null) {
    return 'Just now';
  }

  try {
    return formatDistanceToNow(parsed, { addSuffix: true });
  } catch (error) {
    console.error('Failed to format relative time', error);
    return 'Recently';
  }
}

function renderTimelineDetails(event: LeadTimelineEvent): ReactNode {
  const payload = event.payload;

  switch (event.eventType) {
    case 'captured': {
      const stageLabel = formatTimelineStageLabel(payload.stage);
      const campaignSource = safeString(payload.campaignSource);
      const campaignMedium = safeString(payload.campaignMedium);
      const hasCampaignMetadata = campaignSource !== null || campaignMedium !== null;

      return (
        <>
          {stageLabel !== null ? (
            <StatusBadge label={stageLabel} tone="info" className="max-w-fit" />
          ) : (
            <span className="text-sm">Lead captured.</span>
          )}
          {hasCampaignMetadata ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-primary/60">
              {campaignSource !== null ? (
                <span>
                  <span className="font-semibold text-text-primary/70">Source:</span>{' '}
                  <span className="text-text-primary">{campaignSource}</span>
                </span>
              ) : null}
              {campaignMedium !== null ? (
                <span>
                  <span className="font-semibold text-text-primary/70">Medium:</span>{' '}
                  <span className="text-text-primary">{campaignMedium}</span>
                </span>
              ) : null}
            </div>
          ) : null}
        </>
      );
    }
    case 'stage_change': {
      const stageLabel = formatTimelineStageLabel(payload.stage) ?? 'Stage updated';
      const previousStage = formatTimelineStageLabel(payload.previousStage);

      return (
        <StatusBadge
          label={stageLabel}
          tone="info"
          secondaryLabel={previousStage !== null ? `Prev: ${previousStage}` : null}
          secondaryTone="neutral"
          className="max-w-fit"
        />
      );
    }
    case 'follow_up_set': {
      const nextFollowUp = formatTimelineDateTime(payload.nextFollowUpAt);
      const previousFollowUp = formatTimelineDateTime(payload.previousNextFollowUpAt);

      return (
        <div className="flex flex-col gap-1">
          <span className="font-medium">
            {nextFollowUp !== null ? `Next follow-up: ${nextFollowUp}` : 'Follow-up scheduled.'}
          </span>
          {previousFollowUp !== null ? (
            <span className="text-xs text-text-primary/60">
              Previous reminder: {previousFollowUp}
            </span>
          ) : null}
        </div>
      );
    }
    case 'follow_up_cleared': {
      const previousFollowUp = formatTimelineDateTime(payload.previousNextFollowUpAt);
      return (
        <div className="flex flex-col gap-1">
          <span className="font-medium">Follow-up reminder cleared.</span>
          {previousFollowUp !== null ? (
            <span className="text-xs text-text-primary/60">
              Previous reminder: {previousFollowUp}
            </span>
          ) : null}
        </div>
      );
    }
    case 'contact_logged': {
      const contactedAt = formatTimelineDateTime(payload.contactedAt);
      const nextFollowUp = formatTimelineDateTime(payload.nextFollowUpAt);

      return (
        <div className="flex flex-col gap-1">
          <span className="font-medium">
            {contactedAt !== null ? `Contact recorded at ${contactedAt}` : 'Contact recorded.'}
          </span>
          {nextFollowUp !== null ? (
            <span className="text-xs text-text-primary/60">Next follow-up: {nextFollowUp}</span>
          ) : null}
        </div>
      );
    }
    case 'assignment': {
      const assignedTo = formatAssignmentName(payload.assignedToDisplayName, payload.assignedTo);
      const previousAssignedTo = formatAssignmentName(
        payload.previousAssignedToDisplayName,
        payload.previousAssignedTo,
      );

      return (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{assignedTo ?? 'Unassigned'}</span>
          {previousAssignedTo !== null ? (
            <span className="text-xs text-text-primary/60">Previous: {previousAssignedTo}</span>
          ) : null}
        </div>
      );
    }
    case 'note': {
      const noteText = safeStringPreserveWhitespace(payload.notes);
      return (
        <blockquote className="whitespace-pre-line rounded-xl bg-surface-light px-4 py-3 text-sm text-text-primary/80">
          {noteText ?? 'Notes updated.'}
        </blockquote>
      );
    }
    default:
      return <span>Lead activity recorded.</span>;
  }
}

function formatTimelineStageLabel(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) {
    return formatLeadStage(value as LeadStage);
  }
  return null;
}

function formatTimelineDateTime(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const formatted = formatDateTime(value);
  return formatted === '—' ? null : formatted;
}

function formatAssignmentName(displayName: unknown, identifier: unknown): string | null {
  const normalizedDisplayName = safeString(displayName);
  if (normalizedDisplayName !== null) {
    return normalizedDisplayName;
  }
  const normalizedIdentifier = safeString(identifier);
  if (normalizedIdentifier !== null) {
    return normalizedIdentifier;
  }
  return null;
}

function safeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function safeStringPreserveWhitespace(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return value;
}

export { LeadTimeline as LeadTimelineInternal, groupTimelineEvents as groupTimelineEventsInternal };

function LeadDetailSkeleton(): JSX.Element {
  return (
    <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
      <Skeleton className="h-80 w-full rounded-3xl" aria-label="Loading lead" />
      <Skeleton className="h-64 w-full rounded-3xl" />
    </div>
  );
}

function LeadDetailError(): JSX.Element {
  return (
    <div className="rounded-3xl border border-rose-200 bg-rose-50 px-6 py-8 text-center text-sm text-rose-700">
      We couldn’t load this lead. Check your permissions or try again later.
    </div>
  );
}

function formatDateTimeLocalInput(value: string | null): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (unit: number) => unit.toString().padStart(2, '0');
  const year = String(date.getFullYear());
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function parseDateTimeLocalInput(value: string): string | null {
  if (value.length === 0) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}
